import axiosClient from './axiosClient';
import categoriesApi from './categoriesApi';
import establishmentsApi from './establishmentsApi';
import menusApi from './menusApi';
import productsApi from './productsApi';
import qrPrintApi from './qrPrintApi';
import userManagementApi from './userManagementApi';

const RESULT_LIMIT = 5;

// Extract a list from the API response formats currently used by the project.
const extractRecords = (response) => {
  const payload = response?.data;

  const candidates = [
    payload?.data?.data,
    payload?.data?.items,
    payload?.data?.records,
    payload?.data,
    payload?.items,
    payload?.records,
    payload,
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
};

// Run search requests independently so one unavailable module never breaks global search.
const runRequests = async (requests) => {
  const entries = Object.entries(requests);

  const settled = await Promise.allSettled(
    entries.map(([, request]) => request()),
  );

  return entries.reduce((result, [key], index) => {
    const current = settled[index];

    result[key] =
      current.status === 'fulfilled'
        ? extractRecords(current.value)
        : [];

    return result;
  }, {});
};

const searchRestaurantOrders = (establishmentId, query) => {
  return axiosClient.get(
    `/establishments/${establishmentId}/orders`,
    {
      params: {
        search: query,
        per_page: RESULT_LIMIT,
      },
    },
  );
};

const searchManagerData = (establishmentId, query) => {
  return runRequests({
    products: () =>
      productsApi.getProducts(establishmentId, {
        search: query,
        per_page: RESULT_LIMIT,
      }),

    categories: () =>
      categoriesApi.getCategories(establishmentId, {
        search: query,
        per_page: RESULT_LIMIT,
      }),

    menus: () =>
      menusApi.getMenus(establishmentId, {
        search: query,
        per_page: RESULT_LIMIT,
      }),

    supplements: () =>
      productsApi.getSupplements(establishmentId, {
        search: query,
        per_page: RESULT_LIMIT,
      }),

    orders: () =>
      searchRestaurantOrders(establishmentId, query),

    users: () =>
      userManagementApi.getStaff(establishmentId, {
        search: query,
        per_page: RESULT_LIMIT,
      }),

    qrProducts: () =>
      qrPrintApi.getProducts({
        search: query,
        per_page: RESULT_LIMIT,
      }),

    qrOrders: () =>
      qrPrintApi.getOrders({
        search: query,
        per_page: RESULT_LIMIT,
      }),
  });
};

const searchSuperAdminData = (query) => {
  return runRequests({
    establishments: () =>
      establishmentsApi.getEstablishments({
        search: query,
        per_page: RESULT_LIMIT,
      }),

    users: () =>
      userManagementApi.getManagers({
        search: query,
        per_page: RESULT_LIMIT,
      }),

    qrProducts: () =>
      qrPrintApi.getProducts({
        search: query,
        per_page: RESULT_LIMIT,
      }),

    qrOrders: () =>
      qrPrintApi.getOrders({
        search: query,
        per_page: RESULT_LIMIT,
      }),
  });
};

const searchOrdersOnlyData = (establishmentId, query) => {
  return runRequests({
    orders: () =>
      searchRestaurantOrders(establishmentId, query),
  });
};

export const globalSearchApi = {
  async search({
    query,
    mode,
    establishmentId,
  }) {
    const normalizedQuery = String(query || '').trim();

    if (!normalizedQuery) {
      return {};
    }

    if (mode === 'superAdmin') {
      return searchSuperAdminData(normalizedQuery);
    }

    if (mode === 'manager' && establishmentId) {
      return searchManagerData(
        establishmentId,
        normalizedQuery,
      );
    }

    if (mode === 'ordersOnly' && establishmentId) {
      return searchOrdersOnlyData(
        establishmentId,
        normalizedQuery,
      );
    }

    return {};
  },
};

export default globalSearchApi;